import { useEffect, useRef, useState } from 'react'
import { Disc3, ExternalLink, Music } from 'lucide-react'
import { fetchNowPlaying } from '../lib/api'
import type { SpotifyNowPlaying } from '../lib/api'

export default function NowPlayingTile() {
  const [data, setData] = useState<SpotifyNowPlaying | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const poll = () => fetchNowPlaying().then(setData).catch(() => {})
    poll()
    intervalRef.current = setInterval(poll, 10_000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  if (!data) {
    return (
      <div className="life-card-warm rounded-xl p-6 sm:p-8 flex items-center gap-5">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-amber-400/10 flex items-center justify-center flex-shrink-0">
          <Music size={28} className="text-amber-300/30" />
        </div>
        <div className="space-y-2">
          <div className="h-3.5 w-32 bg-amber-400/10 rounded animate-pulse" />
          <div className="h-3 w-24 bg-amber-400/10 rounded animate-pulse" />
        </div>
      </div>
    )
  }

  const { isPlaying, progress, duration, track } = data

  if (!track) {
    return (
      <div className="life-card-warm rounded-xl p-6 sm:p-8 flex items-center gap-5">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-amber-400/10 flex items-center justify-center flex-shrink-0">
          <Music size={28} className="text-amber-300/30" />
        </div>
        <p className="text-amber-200/40 text-sm">Nothing playing right now</p>
      </div>
    )
  }

  const progressPct = progress != null && duration ? (progress / duration) * 100 : 0

  return (
    <div className="life-card-warm rounded-xl p-6 sm:p-8">
      <div className="flex items-start gap-5">
        {track.albumArt ? (
          <img
            src={track.albumArt}
            alt={track.album}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover flex-shrink-0 shadow-lg shadow-amber-900/30"
          />
        ) : (
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-amber-400/10 flex items-center justify-center flex-shrink-0">
            <Disc3 size={28} className="text-amber-300/60" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isPlaying ? (
              <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Now Playing
              </span>
            ) : (
              <span className="text-xs uppercase tracking-wider text-amber-200/40">
                Last Played
              </span>
            )}
          </div>
          <p className="text-amber-50 text-lg font-heading font-medium truncate">{track.name}</p>
          <p className="text-amber-200/70 text-sm truncate">{track.artist}</p>
          {track.spotifyUrl && (
            <a
              href={track.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-amber-300/60 hover:text-amber-300 transition-colors mt-1"
            >
              <ExternalLink size={11} />
              Open in Spotify
            </a>
          )}
        </div>
      </div>

      {isPlaying && duration != null && (
        <div className="mt-4">
          <div className="h-1 rounded-full bg-amber-400/10 overflow-hidden">
            <div
              className="h-full bg-amber-400/60 transition-all duration-1000"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
